import { AxiosError, AxiosPromise, AxiosResponse } from 'axios'
import { Delete } from './Delete'
import { Get } from './Get'
import { Post } from './Post'
import { Put } from './Put'
import { ApiHelpers, APIHosts } from './ApiHelpers'
import { RemoveRoleFromUsers } from './OrganizationService'

export interface StaffInfo {
  userOrgId: string
  organizationRoles: string[]
  id: string
  invitationStatus: string
  userName: string
  userCode: string
  email: string
  phoneNumber: string | null
  firstname: string
  lastname: string
  organizationJobName: string | null
  organizationJobId: string | null
  profile: {
    note: string | null
    checkInDate: string | null
  }
  note: string | null
  checkInDate: string | null
  isActive: boolean
  lockoutEnabled: boolean
}

export interface StaffPayload {
  userCode?: string
  firstname?: string
  lastname?: string
  avatarUrl?: string
  forcePasswordChange?: boolean
  lockoutEnabled?: boolean
  isActive?: boolean
  profile?: {
    gender?: string
    avatarUrl?: string
    workAddress?: string
    workAddress2?: string
    workCity?: string
    workZip?: string
    workState?: string
    workCountry?: string
    address?: string
    address2?: string
    city?: string
    state?: string
    zip?: string
    country?: string
    checkInDate?: string
    note?: string
  }
  password?: string
  userName?: string
  email?: string
  phoneNumber?: string
}

interface UserFilter {
  filter?: string
  expand?: string
  paging?: boolean
  orderBy?: string
  page?: number
  pageSize?: number
  offset?: number
  take?: number
  invitationStatus?: string
  roles?: string
}

export interface StaffRole {
  id: string
  roleId: string
  userOrganizationId: string
  organizationRoles: {
    id: string
    name: string
    normalizedName: string
    organizationId: string
  }
  roleName: string
}

class StaffServiceController extends ApiHelpers {
  ApiHost = APIHosts.LicenseManager

  public Get = {
    getOrganizationUsers: async (
      orgId: string,
      data?: UserFilter,
      roles?: string[],
      invitationStatus?: string[],
    ) => {
      const baseUrl = this.getHost(this.ApiHost)
      let url = `${baseUrl}/organizations/${orgId}/users`
      let rolesParam = ''
      if (roles) {
        rolesParam = `roles=${roles.join('&roles=')}`
      }
      let statusParam = ''
      if (invitationStatus) {
        statusParam = `invitationStatus=${invitationStatus.join(
          '&invitationStatus=',
        )}`
      }
      if (rolesParam || statusParam) {
        url += `?${[rolesParam, statusParam].filter(i => i).join('&')}`
      }

      const filterData = {
        params: {
          ...data,
        },
      }
      return Get(url, filterData)
    },
    getRolesOfUser: (userId: string, orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/users/${userId}/organizationRoles`
      return Get(url, {
        params: { orgId },
      })
    },
    getJobOfUser: (userId: string, orgId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/jobs/${userId}/organizationRoles`
      return Get(url, {
        params: { orgId },
      })
    },
  }

  public Post = {
    createNewStaff: (data: StaffPayload) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/organizations/users/register`, data)
    },
    addRolestoUser: (
      userId: string,
      roleData: { organizationId: string; roleName: string }[],
    ) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/users/${userId}/organizationRoles`, roleData)
    },
    removeRolesFromStaff: (
      userId: string,
      roleData: { organizationId: string; roleName: string }[],
    ): AxiosPromise<AxiosResponse<RemoveRoleFromUsers[]> | AxiosError> => {
      const baseUrl = this.getHost(this.ApiHost)
      return Delete(`${baseUrl}/users/${userId}/organizationRoles`, roleData)
    },
    importStaffs: (orgId: string, file: File) => {
      const baseUrl = this.getHost(this.ApiHost)
      const formData = new FormData()
      formData.append('file', file)
      return Post(`${baseUrl}/organizations/${orgId}/users/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
  }

  public Put = {
    updateCheckInDate: (userOrgId: string, checkInDate: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Put(
        `${baseUrl}/organizations/users/${userOrgId}/changeCheckinDate`,
        checkInDate, 
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
    },
  }

  public Delete = {
    removeUsers: (orgId: string, userIds: string[]) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/organizations/${orgId}/users`
      return Delete(url, userIds)
    },
  }
}

export const StaffService = new StaffServiceController()
